import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Flower2, BookOpen, Newspaper, Heart, Download, Eye, Loader2 } from "lucide-react";
import { api, API, COLORS } from "@/lib/api";
import { toast } from "sonner";

const ENCONTRARAS = [
  "Historias de juventudes reales",
  "Herramientas para la vida diaria",
  "Temas que nos importan",
  "Recomendaciones para ver, leer y escuchar",
  "Reflexiones, entrevistas, poesía y más",
  "Anuncios de programas y noticias de la red de clubs",
];

const fileUrl = (v, ref) => (v.is_static ? ref : `${API}/newsletter/file/${ref}`);

const ElJardin = () => {
  const [volumes, setVolumes] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/newsletter/volumes").then((r) => setVolumes(r.data)).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      await api.post("/subscribe", { email: email.trim(), name: name.trim() });
      setDone(true);
      setEmail("");
      setName("");
      toast.success("¡Listo! Te avisaremos en cada nueva siembra 🌱");
    } catch {
      toast.error("No pudimos registrar tu correo. Intenta de nuevo.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="subpage-el-jardin">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#E5E5E5]" style={{ background: `linear-gradient(120deg, ${COLORS.orange} 0%, ${COLORS.magenta} 100%)` }}>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Link to="/programas" data-testid="subpage-back" className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white mb-6">
            <ArrowLeft size={16} /> Programas
          </Link>
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/90 bg-white/20 rounded-full px-3 py-1 mb-5">
            <Newspaper size={12} /> Newsletter de Juventudes Latentes
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[0.98]">El Jardín</h1>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        {/* Leyenda */}
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="flex items-start gap-3 rounded-2xl p-6 mb-10" style={{ background: "#FDE7FA" }}>
          <Flower2 size={26} className="text-brand-magenta flex-shrink-0 mt-0.5" />
          <p className="text-brand-ink/85 leading-relaxed">
            <span className="font-semibold text-brand-ink">El Jardín regresa en 2026.</span> Un otoño para
            sembrar, una primavera para florecer. Bienvenides de vuelta, esperamos disfrutes lo que las
            cuidadoras Sofi y Emi tienen preparado para esta primera siembra de la temporada. 🌸
          </p>
        </motion.div>

        {/* Qué encontrarás */}
        <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-brand-ink">¿Qué encontrarás aquí?</h2>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ENCONTRARAS.map((t) => (
            <div key={t} className="flex items-start gap-2.5 border border-[#E5E5E5] rounded-xl p-4">
              <BookOpen size={16} className="text-brand-magenta flex-shrink-0 mt-0.5" />
              <span className="text-brand-ink/80 text-sm">{t}</span>
            </div>
          ))}
        </div>

        {/* Archivo de ediciones */}
        <div className="mt-14 flex items-baseline justify-between flex-wrap gap-2">
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-brand-ink">Ediciones de El Jardín</h2>
          <span className="text-sm text-brand-ink/50">{volumes.length} {volumes.length === 1 ? "edición" : "ediciones"}</span>
        </div>
        <p className="mt-2 text-brand-ink/70">Explora y lee todos los volúmenes. Aquí se irán guardando las siembras anteriores.</p>

        {volumes.length === 0 ? (
          <p className="mt-6 text-brand-ink/50">Pronto publicaremos la primera edición.</p>
        ) : (
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="jardin-archivo">
            {volumes.map((v) => {
              const pdf = fileUrl(v, v.pdf_ref);
              const cover = fileUrl(v, v.cover_ref);
              return (
                <div
                  key={v.id}
                  data-testid={`jardin-vol-${v.id}`}
                  className="group rounded-2xl overflow-hidden border border-[#E5E5E5] hover:border-brand-magenta transition-all"
                  style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}
                >
                  <a href={pdf} target="_blank" rel="noopener noreferrer" className="block relative aspect-[4/5] overflow-hidden bg-[#F6F6F6]">
                    <img src={cover} alt={`${v.label} — ${v.title}`} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    <span className="absolute top-3 left-3 text-xs font-bold uppercase tracking-wide text-white px-2.5 py-1 rounded-full" style={{ background: COLORS.magenta }}>{v.label}</span>
                  </a>
                  <div className="p-4">
                    <h3 className="font-heading text-lg font-bold tracking-tight text-brand-ink">{v.title}</h3>
                    {v.season && <p className="text-xs text-brand-ink/50">{v.season}</p>}
                    <div className="mt-3 flex gap-2">
                      <a
                        href={pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`jardin-read-${v.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-white px-3 py-2 rounded-full bg-brand-ink hover:bg-brand-magenta transition-colors"
                      >
                        <Eye size={14} /> Leer boletín
                      </a>
                      <a
                        href={pdf}
                        download
                        data-testid={`jardin-download-${v.id}`}
                        className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#E5E5E5] text-brand-ink hover:border-brand-magenta hover:text-brand-magenta transition-colors"
                        aria-label={`Descargar ${v.label}`}
                      >
                        <Download size={16} />
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Suscripción */}
        <div className="mt-14 rounded-2xl bg-brand-ink p-8 md:p-10 text-center" data-testid="jardin-suscripcion">
          <Heart size={26} className="text-brand-magenta mx-auto" fill={COLORS.magenta} />
          <h2 className="mt-3 font-heading text-2xl md:text-3xl font-bold tracking-tight text-white">Suscríbete a El Jardín</h2>
          <p className="mt-2 text-white/75 max-w-xl mx-auto">Recíbelo cada mes en tu correo. Déjanos tus datos y te avisamos en cada nueva siembra.</p>

          {done ? (
            <div data-testid="jardin-subscribe-success" className="mt-6 inline-flex items-center gap-2 bg-brand-lime text-brand-ink font-semibold px-6 py-3 rounded-full">
              <Flower2 size={18} /> ¡Gracias! Ya estás en la lista 🌱
            </div>
          ) : (
            <form onSubmit={subscribe} data-testid="jardin-subscribe-form" className="mt-6 max-w-md mx-auto flex flex-col gap-3">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="jardin-name-input"
                placeholder="Tu nombre (opcional)"
                className="w-full bg-white/10 border border-white/20 text-white placeholder-white/50 rounded-full px-5 py-3 outline-none focus:border-brand-magenta"
              />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="jardin-email-input"
                placeholder="tu@correo.com"
                className="w-full bg-white/10 border border-white/20 text-white placeholder-white/50 rounded-full px-5 py-3 outline-none focus:border-brand-magenta"
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="jardin-subscribe-btn"
                className="inline-flex items-center justify-center gap-2 bg-brand-magenta text-white font-semibold px-6 py-3 rounded-full hover:bg-brand-orange transition-colors disabled:opacity-60"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Flower2 size={16} />}
                {sending ? "Enviando…" : "Quiero suscribirme"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ElJardin;
